import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Clock, CurrencyGbp, ChatCircle, CheckCircle, X, ShieldCheck, Warning, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import DeclineReasonModal from "../components/DeclineReasonModal";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [decliningQuote, setDecliningQuote] = useState(null);
  const [confirmAccept, setConfirmAccept] = useState(null); // quote pending confirmation
  const [contact, setContact] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Once a quote is accepted, fetch shareable contact details (allowed for owner + accepted trade)
  useEffect(() => {
    if (!job || !user) { setContact(null); return; }
    const canSee = user.user_id === job.customer_id
      || (job.accepted_tradesperson_id === user.user_id && job.accepted_quote_id);
    if (!canSee) { setContact(null); return; }
    api.get(`/jobs/${job.job_id}/contact`)
      .then(({ data }) => setContact(data))
      .catch(() => setContact(null));
  }, [job, user]);

  if (loading) return <Layout><div className="max-w-4xl mx-auto p-10 text-[var(--text-muted)]">Loading…</div></Layout>;
  if (!job) return <Layout><div className="max-w-4xl mx-auto p-10">Job not found.</div></Layout>;

  const isOwner = user && user.user_id === job.customer_id;
  const isTrader = user && user.role === "tradesperson";
  const isAssignedTrade = isTrader && job.accepted_tradesperson_id === user.user_id;
  const alreadyQuoted = isTrader && (job.quotes || []).some(q => q.tradesperson_id === user.user_id);
  const canDispute = (isOwner || isAssignedTrade) && (job.status === "in_progress" || job.status === "completed") && !job.disputed;

  const submitQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const a = parseFloat(amount);
      if (!a || a <= 0) { toast.error("Enter a valid amount"); return; }
      await api.post("/quotes", { job_id: job.job_id, amount: a, message });
      toast.success("Quote sent");
      setAmount(""); setMessage("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const acceptQuote = async (q) => {
    // Show confirmation dialog first so the customer knows contact will be shared
    setConfirmAccept(q);
  };

  const confirmAcceptQuote = async () => {
    if (!confirmAccept) return;
    const q = confirmAccept;
    setConfirmAccept(null);
    try {
      await api.post(`/quotes/${q.quote_id}/accept`);
      toast.success("Quote accepted — contact details shared with " + (q.tradesperson_business || q.tradesperson_name));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const declineQuote = async (q, body) => {
    try {
      await api.post(`/quotes/${q.quote_id}/decline`, body || {});
      setDecliningQuote(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markComplete = async () => {
    if (!window.confirm("Mark this job as complete? The customer will be notified and can leave you a review.")) return;
    try {
      await api.post(`/jobs/${job.job_id}/complete`);
      toast.success("Job marked complete — nice work!");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const raiseDispute = async () => {
    const reason = window.prompt("Briefly describe the issue (we review all disputes):");
    if (!reason || reason.trim().length < 5) {
      if (reason !== null) toast.error("Please describe the issue (at least 5 characters)");
      return;
    }
    try {
      await api.post(`/jobs/${job.job_id}/dispute`, { reason: reason.trim() });
      toast.success("Dispute raised — our team will be in touch within 24h");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteJob = async () => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await api.delete(`/jobs/${job.job_id}`);
      toast.success("Job deleted");
      navigate("/dashboard/customer");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", {
        job_id: job.job_id,
        tradesperson_id: job.accepted_tradesperson_id,
        rating: review.rating,
        comment: review.comment,
      });
      toast.success("Review posted");
      setReview({ rating: 5, comment: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const messageTrader = async (traderId) => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/messages", {
        recipient_id: traderId,
        job_id: job.job_id,
        body: `Hi! Regarding "${job.title}".`,
      });
      navigate("/messages");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 space-y-6">
        <article className="brand-card p-7" data-testid="job-detail">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div>
              <h1 className="font-display font-extrabold text-3xl" style={{ color: "var(--brand-green)" }}>{job.title}</h1>
              <div className="mt-2 flex flex-wrap gap-3 text-sm text-[var(--text-muted)]">
                <span className="inline-flex items-center gap-1"><MapPin size={14} /> {job.postcode}</span>
                <span className="inline-flex items-center gap-1"><Clock size={14} /> {new Date(job.created_at).toLocaleString()}</span>
                <span className="inline-flex items-center gap-1">Trade: <strong className="text-[var(--brand-green)]">{job.trade}</strong></span>
                {job.budget && <span className="inline-flex items-center gap-1"><CurrencyGbp size={14} weight="bold" /> £{job.budget.toFixed(2)} budget</span>}
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)] uppercase tracking-wide" data-testid="job-status">{job.status.replace("_", " ")}</span>
          </div>
          <p className="mt-5 text-[var(--text-main)] leading-relaxed whitespace-pre-wrap">{job.description}</p>

          {(job.photos || []).length > 0 && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {job.photos.map((p, i) => (<img key={i} src={p} alt="" className="w-full h-32 object-cover rounded-md border border-[var(--border-stone)]" />))}
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-5 flex flex-wrap gap-2">
            {isAssignedTrade && job.status === "in_progress" && (
              <button onClick={markComplete} className="btn-primary" data-testid="job-complete-btn">
                <CheckCircle size={16} /> Mark as done
              </button>
            )}
            {isOwner && (job.status === "open" || job.status === "cancelled") && (
              <button onClick={deleteJob} className="px-4 py-2 rounded-md font-semibold text-red-700 bg-red-50 hover:bg-red-100 transition-colors inline-flex items-center gap-2" data-testid="job-delete-btn">
                <X size={16} /> Delete job
              </button>
            )}
            {canDispute && (
              <button onClick={raiseDispute} className="px-4 py-2 rounded-md font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 transition-colors inline-flex items-center gap-2 text-sm" data-testid="job-dispute-btn">
                <Warning size={16} /> Report an issue
              </button>
            )}
            {job.disputed && (
              <span className="px-3 py-2 rounded-md text-sm font-semibold text-amber-900 bg-amber-100 inline-flex items-center gap-2" data-testid="job-disputed-badge">
                <Warning size={16} weight="fill" /> Under review
              </span>
            )}
          </div>
        </article>

        {/* Shared contact details — visible to the customer AND the accepted trade */}
        {contact && (contact.phone || contact.email) && (
          <div
            className="brand-card p-5 sm:p-6"
            style={{ background: "#F4FDF7", borderColor: "#CBE7D6" }}
            data-testid="job-contact-card"
          >
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.12)" }}>
                <ShieldCheck size={20} weight="fill" style={{ color: "var(--brand-green)" }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--brand-green)" }}>
                  {isOwner ? "Contact details shared" : "Customer contact details"}
                </div>
                <div className="font-display font-extrabold text-lg mt-0.5" style={{ color: "var(--brand-green)" }}>
                  {contact.name}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}
                      className="inline-flex items-center gap-1.5 font-semibold hover:underline"
                      style={{ color: "var(--brand-green)" }}
                      data-testid="job-contact-phone"
                    >
                      <Phone size={16} weight="fill" style={{ color: "var(--brand-orange)" }} />
                      {contact.phone}
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="inline-flex items-center gap-1.5 font-semibold hover:underline break-all"
                      style={{ color: "var(--brand-green)" }}
                      data-testid="job-contact-email"
                    >
                      <EnvelopeSimple size={16} weight="fill" style={{ color: "var(--brand-orange)" }} />
                      {contact.email}
                    </a>
                  )}
                </div>
                {contact.contact_preference === "phone" && (
                  <div className="mt-2 text-xs font-semibold" style={{ color: "var(--brand-orange)" }}>
                    💡 The customer prefers a phone call.
                  </div>
                )}
                <p className="text-xs text-[var(--text-muted)] mt-2 leading-relaxed">
                  {isOwner
                    ? "These details were shared with the tradesperson when you accepted their quote. Only they can see them — no one else who quoted."
                    : "Shared with you because your quote was accepted. Please try in-app messages first, then call/email only if you can't get a response."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tradesperson quote form */}
        {isTrader && job.status === "open" && !alreadyQuoted && (
          <form onSubmit={submitQuote} className="brand-card p-6" data-testid="quote-form">
            <h3 className="font-display font-semibold text-[var(--brand-green)]">Send a quote</h3>
            <p className="text-sm text-[var(--text-muted)]">Be transparent — include scope, materials, and timing.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="block">
                  <span className="text-xs font-semibold text-[var(--text-muted)]">Amount (£)</span>
                  <input data-testid="quote-amount-input" type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]" />
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block">
                  <span className="text-xs font-semibold text-[var(--text-muted)]">Message</span>
                  <textarea data-testid="quote-message-input" required rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)] resize-y" placeholder="E.g. I can do this Saturday morning…" />
                </label>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-accent mt-4" data-testid="quote-submit-btn">{busy ? "Sending…" : "Send quote"}</button>
          </form>
        )}
        {isTrader && alreadyQuoted && (
          <div className="brand-card p-5 text-sm text-[var(--text-muted)]" data-testid="already-quoted-banner">You&apos;ve already sent a quote on this job.</div>
        )}

        {/* Quotes list visible to owner */}
        {isOwner && (
          <section data-testid="quotes-section">
            <h3 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Quotes ({(job.quotes || []).length})</h3>
            {(job.quotes || []).length === 0 ? (
              <p className="text-[var(--text-muted)] mt-2">No quotes yet — we&apos;ve notified matching trades.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {job.quotes.map((q) => (
                  <div key={q.quote_id} className="brand-card p-5" data-testid={`quote-${q.quote_id}`}>
                    <div className="flex justify-between gap-3 items-start flex-wrap">
                      <div className="flex items-center gap-3">
                        {q.tradesperson_picture ? (
                          <img src={q.tradesperson_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)] font-display font-bold flex items-center justify-center">{q.tradesperson_name[0]}</div>
                        )}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link to={`/trades/${q.tradesperson_id}`} className="font-display font-semibold text-[var(--brand-green)] hover:underline" data-testid={`quote-trader-link-${q.quote_id}`}>{q.tradesperson_business || q.tradesperson_name}</Link>
                            {q.tradesperson_verified && (
                              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]">
                                <ShieldCheck size={10} weight="fill" className="inline -mt-0.5 mr-0.5" /> Verified
                              </span>
                            )}
                            {typeof q.distance_miles === "number" && (
                              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-[rgba(226,109,41,0.12)] text-[var(--brand-orange)]" data-testid={`distance-${q.quote_id}`}>
                                <MapPin size={10} weight="fill" className="inline -mt-0.5 mr-0.5" /> {q.distance_miles} mi away
                              </span>
                            )}
                          </div>
                          <StarRating value={q.tradesperson_rating || 0} size={12} />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-extrabold text-2xl text-[var(--brand-green)]">£{q.amount.toFixed(2)}</div>
                        <div className="text-xs uppercase font-semibold" style={{ color: q.status==="accepted"?"#0A7048":q.status==="declined"?"#8a3030":"var(--brand-orange)" }}>{q.status}</div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-[var(--text-main)] whitespace-pre-wrap">{q.message}</p>
                    {q.status === "pending" && job.status === "open" && (
                      <div className="mt-4 flex gap-2 flex-wrap">
                        <button onClick={() => acceptQuote(q)} className="btn-primary" data-testid={`accept-quote-${q.quote_id}`}><CheckCircle size={16} /> Accept</button>
                        <button onClick={() => setDecliningQuote(q)} className="btn-ghost" data-testid={`decline-quote-${q.quote_id}`}><X size={16} /> Decline</button>
                        <button onClick={() => messageTrader(q.tradesperson_id)} className="btn-ghost" data-testid={`message-quote-${q.quote_id}`}><ChatCircle size={16} /> Message</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Review form */}
        {isOwner && job.status === "completed" && job.accepted_tradesperson_id && (
          <form onSubmit={submitReview} className="brand-card p-6" data-testid="review-form">
            <h3 className="font-display font-semibold text-[var(--brand-green)]">Leave a review</h3>
            <div className="mt-3 flex items-center gap-2">
              {[1,2,3,4,5].map(n => (
                <button key={n} type="button" onClick={() => setReview(r => ({ ...r, rating: n }))} data-testid={`review-star-${n}`}
                  className={`text-2xl ${n <= review.rating ? "star-on" : "star-off"}`}>★</button>
              ))}
            </div>
            <textarea data-testid="review-comment-input" required rows={3} value={review.comment} onChange={(e) => setReview(r => ({ ...r, comment: e.target.value }))} className="w-full mt-3 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)] resize-y" placeholder="How was the work?" />
            <button className="btn-accent mt-3" data-testid="review-submit-btn">Submit review</button>
          </form>
        )}

        <DeclineReasonModal
          open={!!decliningQuote}
          onClose={() => setDecliningQuote(null)}
          onConfirm={(body) => declineQuote(decliningQuote, body)}
          traderName={decliningQuote?.tradesperson_business || decliningQuote?.tradesperson_name}
          amount={decliningQuote?.amount}
        />

        {/* Accept-quote confirmation with contact-sharing notice */}
        {confirmAccept && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(18,28,24,0.55)" }}
            onClick={() => setConfirmAccept(null)}
            data-testid="accept-confirm-overlay"
          >
            <div
              className="brand-card p-6 sm:p-7 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="accept-confirm-modal"
            >
              <div className="flex items-center gap-3">
                <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.10)" }}>
                  <CheckCircle size={22} weight="duotone" style={{ color: "var(--brand-green)" }} />
                </span>
                <h3 className="font-display font-extrabold text-xl" style={{ color: "var(--brand-green)" }}>
                  Accept this quote?
                </h3>
              </div>
              <div className="mt-4 rounded-xl p-4" style={{ background: "#F4FDF7", border: "1px solid #CBE7D6" }}>
                <div className="font-semibold text-sm" style={{ color: "var(--brand-green)" }}>
                  {confirmAccept.tradesperson_business || confirmAccept.tradesperson_name}
                </div>
                <div className="font-display font-extrabold text-2xl mt-1" style={{ color: "var(--brand-green)" }}>
                  £{Number(confirmAccept.amount).toFixed(2)}
                </div>
                {confirmAccept.message && (
                  <p className="text-xs text-[var(--text-muted)] mt-2 line-clamp-2">"{confirmAccept.message}"</p>
                )}
              </div>
              <div className="mt-4 rounded-xl p-4 flex items-start gap-3" style={{ background: "#FFF6EF", border: "1px solid #FFD2AE" }}>
                <ShieldCheck size={22} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: "var(--brand-orange)" }} />
                <div className="text-sm leading-relaxed" style={{ color: "#5A3B1E" }}>
                  <strong>Your contact details will be shared.</strong> As soon as you accept, we'll share your name, phone number and email with this tradesperson — so they can reach you directly if in-app messaging doesn't work. Other tradespeople who quoted will not see your details.
                </div>
              </div>
              <div className="mt-5 flex gap-2 justify-end">
                <button
                  onClick={() => setConfirmAccept(null)}
                  className="btn-ghost"
                  data-testid="accept-confirm-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAcceptQuote}
                  className="btn-primary"
                  data-testid="accept-confirm-yes"
                >
                  <CheckCircle size={16} /> Yes, accept & share
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
